import { useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { IconTile } from '../../../shared/components/IconTile';
import { Pill } from '../../../shared/components/Pill';
import { SEMANTIC_COLOR_VARS, type SemanticColor } from '../../../shared/components/semanticColor';
import type {
  DocumentSectionConfidence,
  ProjectValidationSummaryData,
  ValidationLevel,
  ValidationSummaryStage,
} from '../../../core/types/data-models';
import type { BlockCopy } from '../validationCopy';
import { BAND_COLOR, BAND_LABEL, Bar, LabeledBar, SegmentBar, StatTile, formatScore, scoreBand, type ScoreBand } from './SummaryCharts';

// ── Shared pieces ────────────────────────────────────────────────────────────

/** What every block needs to point back at a stage: its name, whether it can be opened, and how. */
export interface StageLinks {
  nameOf: (docType: string) => string;
  isGenerated: (docType: string) => boolean;
  open: (docType: string) => void;
}

type StagePoint = { text: string; docType: string };

const CONFIDENCE_LABEL: Record<DocumentSectionConfidence, string> = {
  HIGH: 'High confidence',
  MEDIUM: 'Medium confidence',
  LOW: 'Low confidence',
};

export const BAND_ICON: Record<ScoreBand, ReactNode> = {
  strong: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
  mixed: <ErrorOutlineIcon sx={{ fontSize: 14 }} />,
  weak: <CancelOutlinedIcon sx={{ fontSize: 14 }} />,
};

const LEVEL_COLOR: Record<ValidationLevel, SemanticColor> = { HIGH: 'error', MEDIUM: 'warning', LOW: 'success' };
const LEVEL_LABEL: Record<ValidationLevel, string> = { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' };

function BlockHeader({
  icon,
  color,
  title,
  subtitle,
}: {
  icon: ReactNode;
  color: SemanticColor;
  title: string;
  subtitle?: string;
}) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 2 }}>
      <IconTile icon={icon} color={color} size="sm" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function Caption({ children, first }: { children: ReactNode; first?: boolean }) {
  return (
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mt: first ? 0 : 2.5, mb: 0.75 }}>
      {children}
    </Typography>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
      {items.map((item) => (
        <Typography key={item} component="li" variant="body2">
          {item}
        </Typography>
      ))}
    </Stack>
  );
}

/** A small clickable chip naming the stage a fact comes from; opens the stage's document. */
function StageChip({ docType, links }: { docType: string; links: StageLinks }) {
  const generated = links.isGenerated(docType);
  return (
    <Tooltip title={generated ? 'Open this stage' : 'This stage is not generated yet'}>
      <span>
        <Chip
          label={links.nameOf(docType)}
          size="small"
          variant="outlined"
          disabled={!generated}
          onClick={() => links.open(docType)}
          sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
        />
      </span>
    </Tooltip>
  );
}

function BandPill({ score }: { score: number }) {
  const band = scoreBand(score);
  return (
    <Pill color={BAND_COLOR[band]} mono={false}>
      {BAND_ICON[band]}
      {BAND_LABEL[band]}
    </Pill>
  );
}

/** A label/value pair for the definition-list rows used by the customer and go-to-market blocks. Hidden when empty. */
function Fact({ label, children }: { label: string; children: string }) {
  if (!children) return null;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' }, gap: { xs: 0.25, sm: 1.5 } }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ pt: '2px' }}>
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

// ── Scorecard ────────────────────────────────────────────────────────────────

function StageRow({
  index,
  stage,
  verdict,
  takeaways,
  links,
}: {
  index: number;
  stage: ValidationSummaryStage;
  verdict: string | undefined;
  takeaways: Record<string, string>;
  links: StageLinks;
}) {
  const [open, setOpen] = useState(false);
  const generated = links.isGenerated(stage.docType);
  return (
    <Box sx={{ py: 1.25 }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr auto', md: 'minmax(200px, 260px) 1fr auto auto' },
          alignItems: 'center',
          columnGap: 2,
          rowGap: 1,
          cursor: 'pointer',
          borderRadius: 'var(--radius)',
          mx: -1,
          px: 1,
          py: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ fontFamily: 'var(--font-mono)', color: 'text.disabled', width: 16, flexShrink: 0 }}
          >
            {index}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {stage.name}
            </Typography>
            {verdict && !open && (
              <Tooltip title={verdict} placement="bottom-start">
                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }} noWrap>
                  {verdict}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Stack>

        <Tooltip title={`${stage.name}: ${formatScore(stage.score)} of 10, ${CONFIDENCE_LABEL[stage.confidence].toLowerCase()}`}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ order: { xs: 3, md: 0 }, gridColumn: { xs: '1 / -1', md: 'auto' } }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Bar fraction={stage.score / 10} color={BAND_COLOR[scoreBand(stage.score)]} />
            </Box>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ width: 32, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
            >
              {formatScore(stage.score)}
            </Typography>
          </Stack>
        </Tooltip>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
          <BandPill score={stage.score} />
          <Pill mono={false}>{CONFIDENCE_LABEL[stage.confidence]}</Pill>
        </Stack>

        <IconButton
          size="small"
          aria-label={open ? `Collapse ${stage.name}` : `Expand ${stage.name}`}
          tabIndex={-1}
          sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform var(--transition)' }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: { xs: 0, md: 4.5 }, pr: 1, pt: 1 }}>
          {verdict && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {verdict}
            </Typography>
          )}
          <Stack spacing={1}>
            {stage.questions.map((q) => {
              const takeaway = takeaways[String(q.sectionId)] ?? q.verdictReason;
              return (
                <Stack key={q.sectionId} direction="row" spacing={1.5} alignItems="flex-start">
                  <Pill color={BAND_COLOR[scoreBand(q.score)]} sx={{ minWidth: 44, justifyContent: 'center', mt: '1px' }}>
                    {q.score}/10
                  </Pill>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {q.title}
                    </Typography>
                    {takeaway && (
                      <Typography variant="body2" color="text.secondary">
                        {takeaway}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
          <Tooltip title={generated ? '' : 'This stage is not generated yet'}>
            <span>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                disabled={!generated}
                onClick={() => links.open(stage.docType)}
                sx={{ mt: 1.5 }}
              >
                Open {stage.name}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Collapse>
    </Box>
  );
}

export function ScorecardBlock({
  stages,
  verdicts,
  takeaways,
  links,
  subtitle,
}: {
  stages: ValidationSummaryStage[];
  verdicts: Record<string, string>;
  takeaways: Record<string, string>;
  links: StageLinks;
  subtitle: string;
}) {
  const questions = stages.flatMap((s) => s.questions);
  const count = (band: ScoreBand) => questions.filter((q) => scoreBand(q.score) === band).length;
  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <BlockHeader
        icon={<LeaderboardOutlinedIcon fontSize="inherit" />}
        color="primary"
        title="Scorecard by stage"
        subtitle={subtitle}
      />
      <Box sx={{ mb: 2 }}>
        <Caption first>{questions.length} questions answered</Caption>
        <SegmentBar
          segments={[
            { label: 'strong (7–10)', value: count('strong'), color: 'success', icon: BAND_ICON.strong },
            { label: 'mixed (4–6.9)', value: count('mixed'), color: 'warning', icon: BAND_ICON.mixed },
            { label: 'weak (1–3.9)', value: count('weak'), color: 'error', icon: BAND_ICON.weak },
          ]}
        />
      </Box>
      <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
        {stages.map((stage, i) => (
          <StageRow
            key={stage.docType}
            index={i + 1}
            stage={stage}
            verdict={verdicts[stage.docType]}
            takeaways={takeaways}
            links={links}
          />
        ))}
      </Stack>
    </GlassPanel>
  );
}

// ── Strengths and risks ──────────────────────────────────────────────────────

function PointsBlock({
  icon,
  color,
  title,
  subtitle,
  points,
  links,
}: {
  icon: ReactNode;
  color: 'success' | 'error';
  title: string;
  subtitle: string;
  points: StagePoint[];
  links: StageLinks;
}) {
  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <BlockHeader icon={icon} color={color} title={title} subtitle={subtitle} />
      {points.length === 0 ? (
        <Typography variant="body2" color="text.disabled">
          Nothing stood out here.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {points.map((p) => (
            <Stack key={p.text} direction="row" spacing={1.25} alignItems="flex-start">
              <Box sx={{ color: SEMANTIC_COLOR_VARS[color].main, mt: '3px', flexShrink: 0 }}>
                {color === 'success' ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                ) : (
                  <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2">{p.text}</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StageChip docType={p.docType} links={links} />
                </Box>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </GlassPanel>
  );
}

export function StrengthsBlock({ points, links, copy }: { points: StagePoint[]; links: StageLinks; copy: BlockCopy }) {
  return (
    <PointsBlock
      icon={<ThumbUpOutlinedIcon fontSize="inherit" />}
      color="success"
      title={copy.title}
      subtitle={copy.subtitle}
      points={points}
      links={links}
    />
  );
}

export function RisksBlock({ points, links, copy }: { points: StagePoint[]; links: StageLinks; copy: BlockCopy }) {
  return (
    <PointsBlock
      icon={<WarningAmberOutlinedIcon fontSize="inherit" />}
      color="error"
      title={copy.title}
      subtitle={copy.subtitle}
      points={points}
      links={links}
    />
  );
}

// ── Key numbers ──────────────────────────────────────────────────────────────

export function MetricsBlock({ metrics, links }: { metrics: ProjectValidationSummaryData['metrics']; links: StageLinks }) {
  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <BlockHeader
        icon={<QueryStatsOutlinedIcon fontSize="inherit" />}
        color="secondary"
        title="Key numbers"
        subtitle="The figures the decision rests on. Each opens the stage it was taken from."
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 1.5,
        }}
      >
        {metrics.map((m) => (
          <StatTile
            key={m.label}
            label={m.label}
            value={m.value}
            detail={m.detail}
            footer={<StageChip docType={m.docType} links={links} />}
          />
        ))}
      </Box>
    </GlassPanel>
  );
}

// ── Market and demand ────────────────────────────────────────────────────────

export function MarketBlock({ market, copy }: { market: NonNullable<ProjectValidationSummaryData['market']>; copy: BlockCopy }) {
  const maxPct = Math.max(1, ...market.growth.map((g) => g.pct)) * 1.15;
  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <BlockHeader icon={<TrendingUpOutlinedIcon fontSize="inherit" />} color="cyan" title={copy.title} subtitle={copy.subtitle} />
      {market.sizes.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(3, market.sizes.length)}, 1fr)` }, gap: 1.5 }}>
          {market.sizes.map((s) => (
            <StatTile key={s.label} label={s.label} value={s.value} detail={s.detail} />
          ))}
        </Box>
      )}
      {market.growth.length > 0 && (
        <>
          <Caption first={market.sizes.length === 0}>Annual growth rate</Caption>
          <Stack spacing={0.5}>
            {market.growth.map((g) => (
              <LabeledBar
                key={g.label}
                label={g.label}
                valueLabel={`${g.pct}%`}
                fraction={g.pct / maxPct}
                color="primary"
                tooltip={`${g.label}: ${g.pct}% a year`}
              />
            ))}
          </Stack>
        </>
      )}
      {market.demandSignals.length > 0 && (
        <>
          <Caption first={market.sizes.length === 0 && market.growth.length === 0}>Demand signals</Caption>
          <BulletList items={market.demandSignals} />
        </>
      )}
    </GlassPanel>
  );
}

// ── Unit economics ───────────────────────────────────────────────────────────

const LTV_CAC_HEALTHY = 3;

export function EconomicsBlock({ economics }: { economics: NonNullable<ProjectValidationSummaryData['economics']> }) {
  const ltvCacScale = economics.ltvCac ? Math.max(LTV_CAC_HEALTHY * 2, ...economics.ltvCac) * 1.05 : 1;
  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <BlockHeader
        icon={<PaidOutlinedIcon fontSize="inherit" />}
        color="warning"
        title="Unit economics"
        subtitle="Whether one customer pays back the cost of winning and serving them, per scenario."
      />
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { px: 1, whiteSpace: 'nowrap' }, '& th': { bgcolor: 'transparent' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 0 }}>Metric</TableCell>
              {economics.scenarios.map((s) => (
                <TableCell key={s} align="right">
                  {s}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {economics.rows.map((row) => (
              <TableRow key={row.metric}>
                <TableCell sx={{ pl: 0, color: 'text.secondary' }}>{row.metric}</TableCell>
                {economics.scenarios.map((_, i) => (
                  <TableCell key={i} align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {row.values[i] ?? '—'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {economics.ltvCac && (
        <>
          <Caption>LTV/CAC by scenario, against the 3x rule of thumb</Caption>
          <Stack spacing={0.5}>
            {economics.scenarios.map((s, i) => (
              <LabeledBar
                key={s}
                label={s}
                labelWidth={90}
                valueLabel={`${economics.ltvCac![i]}x`}
                fraction={economics.ltvCac![i] / ltvCacScale}
                color="primary"
                marker={LTV_CAC_HEALTHY / ltvCacScale}
                tooltip={`${s}: lifetime value is ${economics.ltvCac![i]}× the acquisition cost`}
              />
            ))}
          </Stack>
        </>
      )}
      {economics.breakPoint && (
        <Box
          sx={{
            mt: 2.5,
            p: 1.5,
            borderRadius: 'var(--radius)',
            bgcolor: 'var(--color-warning-soft)',
            border: '1px solid var(--color-warning-line)',
          }}
        >
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.25 }}>
            Break point
          </Typography>
          <Typography variant="body2">{economics.breakPoint}</Typography>
        </Box>
      )}
    </GlassPanel>
  );
}

// ── Target customer ──────────────────────────────────────────────────────────

const SWITCH_COLOR: Record<ValidationLevel, SemanticColor> = { HIGH: 'success', MEDIUM: 'warning', LOW: 'error' };

export function CustomerBlock({ customer, copy }: { customer: NonNullable<ProjectValidationSummaryData['customer']>; copy: BlockCopy }) {
  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <BlockHeader icon={<GroupsOutlinedIcon fontSize="inherit" />} color="secondary" title={copy.title} subtitle={copy.subtitle} />
      <Typography variant="h6" fontWeight={700} lineHeight={1.25}>
        {customer.primary}
      </Typography>
      {customer.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {customer.description}
        </Typography>
      )}
      <Stack spacing={1.25} sx={{ mt: 2 }}>
        <Fact label="Daily user">{customer.user}</Fact>
        <Fact label="Economic buyer">{customer.buyer}</Fact>
        <Fact label="Segment size">{customer.segmentSize}</Fact>
        <Fact label="Reachability">{customer.reachability}</Fact>
      </Stack>
      {customer.switchLikelihood.length > 0 && (
        <>
          <Caption>Likelihood to switch</Caption>
          <Stack spacing={0.75}>
            {customer.switchLikelihood.map((s) => (
              <Stack key={s.segment} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="body2">{s.segment}</Typography>
                <Pill color={SWITCH_COLOR[s.likelihood]} mono={false}>
                  {LEVEL_LABEL[s.likelihood]}
                </Pill>
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </GlassPanel>
  );
}

// ── Competition ──────────────────────────────────────────────────────────────

const MIX_COLORS: SemanticColor[] = ['primary', 'cyan', 'secondary', 'rose', 'warning'];

export function CompetitionBlock({
  competition,
  copy,
}: {
  competition: NonNullable<ProjectValidationSummaryData['competition']>;
  copy: BlockCopy;
}) {
  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <BlockHeader icon={<StorefrontOutlinedIcon fontSize="inherit" />} color="rose" title={copy.title} subtitle={copy.subtitle} />
      {competition.todayMix && (
        <>
          <Caption first>Where the target segment is today</Caption>
          <SegmentBar
            unit="%"
            segments={competition.todayMix.map((m, i) => ({ label: m.label, value: m.pct, color: MIX_COLORS[i % MIX_COLORS.length] }))}
          />
        </>
      )}
      <Caption first={!competition.todayMix}>Competitors by threat</Caption>
      <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
        {competition.competitors.map((c) => (
          <Stack key={c.name} direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {c.name}
              </Typography>
              {(c.category || c.price) && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {[c.category, c.price].filter(Boolean).join(' · ')}
                </Typography>
              )}
            </Box>
            <Pill color={LEVEL_COLOR[c.threat]} mono={false} sx={{ flexShrink: 0 }}>
              {LEVEL_LABEL[c.threat]} threat
            </Pill>
          </Stack>
        ))}
      </Stack>
      {competition.unmetNeeds.length > 0 && (
        <>
          <Caption>Unmet needs across all of them</Caption>
          <BulletList items={competition.unmetNeeds} />
        </>
      )}
    </GlassPanel>
  );
}

// ── Go-to-market ─────────────────────────────────────────────────────────────

export function GtmBlock({
  gtm,
  copy,
}: {
  gtm: NonNullable<ProjectValidationSummaryData['gtm']>;
  copy: BlockCopy & { triggerLabel: string };
}) {
  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <BlockHeader icon={<RouteOutlinedIcon fontSize="inherit" />} color="cyan" title={copy.title} subtitle={copy.subtitle} />
      <Typography variant="body2" sx={{ mb: gtm.funnel.length > 0 ? 2 : 0 }}>
        {gtm.motion}
      </Typography>
      {gtm.funnel.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${Math.min(4, gtm.funnel.length)}, 1fr)` }, gap: 1.5 }}>
          {gtm.funnel.map((step, i) => (
            <StatTile key={`${i}-${step.label}`} label={`${i + 1}. ${step.label}`} value={step.value} />
          ))}
        </Box>
      )}
      {(gtm.salesCycle || gtm.trigger) && (
        <Stack spacing={1.25} sx={{ mt: 2.5 }}>
          <Fact label="Sales cycle">{gtm.salesCycle}</Fact>
          <Fact label={copy.triggerLabel}>{gtm.trigger}</Fact>
        </Stack>
      )}
    </GlassPanel>
  );
}

// ── Experiments ──────────────────────────────────────────────────────────────

export function ExperimentsBlock({
  experiments,
  links,
  copy,
}: {
  experiments: ProjectValidationSummaryData['experiments'];
  links: StageLinks;
  copy: BlockCopy;
}) {
  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <BlockHeader icon={<ScienceOutlinedIcon fontSize="inherit" />} color="primary" title={copy.title} subtitle={copy.subtitle} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 1.5 }}>
        {experiments.map((e, i) => (
          <Box
            key={e.assumption}
            sx={{
              p: 2,
              borderRadius: 'var(--radius-md)',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: 'var(--color-primary-soft)',
                  color: 'var(--color-primary)',
                  border: '1px solid var(--color-primary-line)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: 'var(--font-mono)',
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </Box>
              <Typography variant="subtitle2" fontWeight={700} lineHeight={1.3}>
                {e.assumption}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {e.test}
            </Typography>
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 700 }}>
                Pass:
              </Box>{' '}
              {e.passBar}
            </Typography>
            <Box sx={{ mt: 'auto', pt: 0.5 }}>
              <StageChip docType={e.docType} links={links} />
            </Box>
          </Box>
        ))}
      </Box>
    </GlassPanel>
  );
}

// ── Traction (business validation) ───────────────────────────────────────────

export function TractionBlock({ traction }: { traction: NonNullable<ProjectValidationSummaryData['traction']> }) {
  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <BlockHeader
        icon={<InsightsOutlinedIcon fontSize="inherit" />}
        color="success"
        title="Traction & growth"
        subtitle="What the company has actually achieved, from public signals, and whether the momentum holds."
      />
      {traction.signals.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: `repeat(${Math.min(3, traction.signals.length)}, 1fr)` },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          {traction.signals.map((sig) => (
            <StatTile key={sig.label} label={sig.label} value={sig.value} detail={sig.detail} />
          ))}
        </Box>
      )}
      <Stack spacing={1.25}>
        <Fact label="Customers">{traction.customers}</Fact>
        <Fact label="Revenue">{traction.revenue}</Fact>
        <Fact label="Growth">{traction.growth}</Fact>
        <Fact label="Retention">{traction.retention}</Fact>
        <Fact label="Funding">{traction.funding}</Fact>
        <Fact label="Momentum">{traction.momentum}</Fact>
      </Stack>
    </GlassPanel>
  );
}

// ── Valuation and deal (business validation) ─────────────────────────────────

const SEVERITY_LABEL: Record<ValidationLevel, string> = { HIGH: 'Deal-breaker', MEDIUM: 'Serious', LOW: 'Minor' };

export function DealBlock({ deal }: { deal: NonNullable<ProjectValidationSummaryData['deal']> }) {
  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <BlockHeader
        icon={<AccountBalanceOutlinedIcon fontSize="inherit" />}
        color="warning"
        title="Valuation & deal"
        subtitle="The valuation frame from comparable companies, the credible exit paths, and the red flags found."
      />
      {deal.valuationRange && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 360px)' } }}>
          <StatTile
            label="Implied valuation range"
            value={deal.valuationRange}
            detail="An estimate from the base-case revenue and the comparable multiples, to be confirmed in the data room."
          />
        </Box>
      )}
      {deal.comparables.length > 0 && (
        <>
          <Caption first={!deal.valuationRange}>Comparables</Caption>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ '& td, & th': { px: 1 }, '& th': { bgcolor: 'transparent' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 0 }}>Company</TableCell>
                  <TableCell align="right">Multiple</TableCell>
                  <TableCell>Deal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deal.comparables.map((c) => (
                  <TableRow key={c.name}>
                    <TableCell sx={{ pl: 0, fontWeight: 600, whiteSpace: 'nowrap' }}>{c.name}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {c.multiple}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{c.detail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}
      {deal.exitPaths.length > 0 && (
        <>
          <Caption first={!deal.valuationRange && deal.comparables.length === 0}>Exit paths</Caption>
          <BulletList items={deal.exitPaths} />
        </>
      )}
      {deal.redFlags.length > 0 && (
        <>
          <Caption first={!deal.valuationRange && deal.comparables.length === 0 && deal.exitPaths.length === 0}>Red flags</Caption>
          <Stack spacing={1}>
            {deal.redFlags.map((flag) => (
              <Stack key={flag.text} direction="row" spacing={1.25} alignItems="flex-start">
                <Pill color={LEVEL_COLOR[flag.severity]} mono={false} sx={{ flexShrink: 0, mt: '1px' }}>
                  {SEVERITY_LABEL[flag.severity]}
                </Pill>
                <Typography variant="body2">{flag.text}</Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </GlassPanel>
  );
}

// ── Price frame (stock validation) ───────────────────────────────────────────

const CALL_COLOR: Record<string, SemanticColor> = { BUY: 'success', HOLD: 'warning', SELL: 'error' };

export function StockBlock({ stock }: { stock: NonNullable<ProjectValidationSummaryData['stock']> }) {
  const tiles = [
    { label: 'Share price', value: stock.price, detail: stock.horizon ? `Horizon: ${stock.horizon}` : undefined },
    { label: 'Fair value (base case)', value: stock.fairValue, detail: 'An estimate from the Valuation stage.' },
    { label: 'Buy below', value: stock.buyBelow, detail: 'Where the base case offers a margin of safety.' },
    { label: 'Sell above', value: stock.sellAbove, detail: 'Where the price already assumes the bull case.' },
  ].filter((t): t is { label: string; value: string; detail: string | undefined } => Boolean(t.value));

  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <BlockHeader
        icon={<PaidOutlinedIcon fontSize="inherit" />}
        color="success"
        title="Price & valuation"
        subtitle="Where the share price stands against the fair value, the prices that change the call, and what could move it."
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: `repeat(${tiles.length}, 1fr)` }, gap: 1.5 }}>
        {tiles.map((t) => (
          <StatTile key={t.label} label={t.label} value={t.value} detail={t.detail} />
        ))}
      </Box>
      {stock.scenarios.length > 0 && (
        <>
          <Caption>Scenarios per share</Caption>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${stock.scenarios.length}, 1fr)` }, gap: 1.5 }}>
            {stock.scenarios.map((s) => (
              <StatTile key={s.label} label={s.label} value={s.value} detail={s.detail} />
            ))}
          </Box>
        </>
      )}
      {stock.catalysts.length > 0 && (
        <>
          <Caption>Catalysts</Caption>
          <Stack spacing={1}>
            {stock.catalysts.map((c) => (
              <Stack key={c.text} direction="row" spacing={1.25} alignItems="flex-start">
                {c.timing && (
                  <Pill mono={false} sx={{ flexShrink: 0, mt: '1px' }}>
                    {c.timing}
                  </Pill>
                )}
                <Typography variant="body2">{c.text}</Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}
      {stock.triggers.length > 0 && (
        <>
          <Caption>What would change the call</Caption>
          <Stack spacing={1.25}>
            {stock.triggers.map((t) => (
              <Stack key={t.condition} direction="row" spacing={1.25} alignItems="flex-start">
                <Pill color={CALL_COLOR[t.changeTo] ?? 'primary'} mono={false} sx={{ flexShrink: 0, mt: '1px' }}>
                  {t.changeTo.charAt(0) + t.changeTo.slice(1).toLowerCase()}
                </Pill>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2">{t.condition}</Typography>
                  {t.signal && (
                    <Typography variant="caption" color="text.secondary">
                      Watch: {t.signal}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </GlassPanel>
  );
}
